import type { JsonDiffSummary, JsonPathDiffEntry } from "./siteContent";

// 관리자 편집 상태는 깊은 복사로 다뤄 기존 site_config 원문 shape를 안전하게 유지한다.
export function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

// 다중 입력 textarea는 줄 단위 배열로 변환해 저장 형식과 맞춘다.
export function linesToList(raw: string) {
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

// 문자열 배열을 textarea 표시용 개행 문자열로 변환한다.
export function listToText(list: string[]) {
  return list.join("\n");
}

// YouTube 탭은 저장 배열을 textarea 친화적인 문자열 상태로 바꿔 사용한다.
export function buildYoutubeTextState(urls: { about_us: { urls: string[] }; videos: { urls: string[] } }) {
  return {
    about: listToText(urls.about_us.urls),
    videos: listToText(urls.videos.urls),
  };
}

// History 항목 배열을 textarea 입력 형태로 직렬화한다.
export function buildHistoryTextarea(contents: string[]) {
  return contents.join("\n");
}

// 공개 화면 미리보기를 위해 일반 URL을 embed URL로 최대한 정규화한다.
export function toYoutubeEmbed(url: string) {
  const value = String(url || "").trim();
  if (!value) return "";
  if (value.includes("/embed/")) return value;

  try {
    const parsed = new URL(value);
    if (parsed.hostname === "youtu.be") {
      const id = parsed.pathname.replace(/^\/+/, "");
      return id ? `https://www.youtube.com/embed/${id}` : value;
    }

    if (parsed.hostname.includes("youtube.com")) {
      const watchId = parsed.searchParams.get("v");
      return watchId ? `https://www.youtube.com/embed/${watchId}` : value;
    }
  } catch {
    return value;
  }

  return value;
}

// API 시간 문자열을 datetime-local input 값(yyyy-MM-ddTHH:mm)으로 변환한다.
export function formatDateTimeLocalInput(value: string | null | undefined) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const pad = (num: number) => String(num).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

// 클라이언트 전용 임시 식별자를 prefix와 함께 생성한다.
export function createClientId(prefix = "id") {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

// YouTube 입력 URL이 watch/embed/short 링크 규칙을 만족하는지 검증한다.
export function isValidYoutubeUrl(url: string) {
  const value = String(url || "").trim();
  if (!value) return false;
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    if (host === "youtu.be") return parsed.pathname.replace(/^\/+/, "").length > 0;
    if (!host.includes("youtube.com")) return false;
    return parsed.pathname.includes("/embed/") || parsed.pathname === "/watch" || parsed.searchParams.has("v");
  } catch {
    return false;
  }
}

// 내부 저장 형식(YYYY.MM)을 month input 형식(YYYY-MM)으로 변환한다.
export function toHistoryMonthInput(value: string) {
  const text = String(value || "").trim();
  if (!/^\d{4}\.\d{2}$/.test(text)) return "";
  return text.replace(".", "-");
}

// month input 값(YYYY-MM)을 내부 저장 형식(YYYY.MM)으로 변환한다.
export function fromHistoryMonthInput(value: string) {
  const text = String(value || "").trim();
  if (!/^\d{4}-\d{2}$/.test(text)) return "";
  return text.replace("-", ".");
}

// History 연월 문자열이 YYYY.MM 규격과 월 범위를 만족하는지 확인한다.
export function isValidYearMonth(value: string) {
  const text = String(value || "").trim();
  if (!/^\d{4}\.\d{2}$/.test(text)) return false;
  const month = Number(text.split(".")[1]);
  return month >= 1 && month <= 12;
}

// CSV 셀 값의 따옴표 이스케이프와 quote wrapping을 수행한다.
function escapeCsvCell(value: unknown) {
  const text = String(value ?? "");
  const escaped = text.replace(/"/g, "\"\"");
  return `"${escaped}"`;
}

// 브라우저에서 UTF-8 BOM CSV 파일을 생성해 즉시 다운로드한다.
export function downloadCsvFile(filename: string, headers: string[], rows: Array<Array<unknown>>) {
  const safeName = String(filename || "export.csv").replace(/[\\/:*?"<>|]+/g, "_");
  const body = rows.map((row) => row.map(escapeCsvCell).join(",")).join("\n");
  const csv = `\uFEFF${headers.map(escapeCsvCell).join(",")}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

type SafeParseResult = {
  ok: boolean;
  value?: unknown;
  error?: string;
};

// JSON diff 계산에서 객체 타입 여부를 안정적으로 판별한다.
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === "[object Object]";
}

// JSON 비교 안정화를 위해 객체 key 정렬 기반으로 값을 재귀 정규화한다.
function normalizeJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeJsonValue(item));
  }
  if (isPlainObject(value)) {
    const normalized: Record<string, unknown> = {};
    Object.keys(value)
      .sort((a, b) => a.localeCompare(b))
      .forEach((key) => {
        normalized[key] = normalizeJsonValue(value[key]);
      });
    return normalized;
  }
  return value;
}

// 정규화된 JSON 문자열을 만들어 구조 동일성 비교 기준으로 사용한다.
function stableStringify(value: unknown): string {
  try {
    return JSON.stringify(normalizeJsonValue(value));
  } catch {
    return String(value);
  }
}

// 두 값을 JSON 구조 기준으로 동등 비교한다.
function valuesEqual(left: unknown, right: unknown): boolean {
  return stableStringify(left) === stableStringify(right);
}

// JSON 문자열 파싱을 시도하고 실패 사유를 함께 반환한다.
export function safeParseJson(raw: string | null | undefined): SafeParseResult {
  const text = String(raw ?? "").trim();
  if (!text) return { ok: true, value: null };

  try {
    return { ok: true, value: normalizeJsonValue(JSON.parse(text)) };
  } catch (caught) {
    return {
      ok: false,
      error: caught instanceof Error ? caught.message : "JSON parse error",
    };
  }
}

// diff path를 객체/배열 규칙에 맞춰 누적 생성한다.
function buildChildPath(parentPath: string, key: string | number, parentIsArray: boolean): string {
  if (parentIsArray) return `${parentPath}[${key}]`;
  return parentPath === "$" ? `$.${String(key)}` : `${parentPath}.${String(key)}`;
}

// JSON 구조를 재귀 순회해 path 단위(added/removed/changed) 차이를 만든다.
function walkJsonDiff(beforeValue: unknown, afterValue: unknown, currentPath: string, entries: JsonPathDiffEntry[]) {
  if (valuesEqual(beforeValue, afterValue)) return;

  if (Array.isArray(beforeValue) && Array.isArray(afterValue)) {
    const maxLength = Math.max(beforeValue.length, afterValue.length);
    for (let index = 0; index < maxLength; index += 1) {
      const hasBefore = index < beforeValue.length;
      const hasAfter = index < afterValue.length;
      const path = buildChildPath(currentPath, index, true);
      if (!hasBefore && hasAfter) {
        entries.push({ type: "added", path, afterValue: afterValue[index] });
      } else if (hasBefore && !hasAfter) {
        entries.push({ type: "removed", path, beforeValue: beforeValue[index] });
      } else {
        walkJsonDiff(beforeValue[index], afterValue[index], path, entries);
      }
    }
    return;
  }

  if (isPlainObject(beforeValue) && isPlainObject(afterValue)) {
    const keys = Array.from(new Set([...Object.keys(beforeValue), ...Object.keys(afterValue)])).sort((a, b) => a.localeCompare(b));
    keys.forEach((key) => {
      const hasBefore = Object.prototype.hasOwnProperty.call(beforeValue, key);
      const hasAfter = Object.prototype.hasOwnProperty.call(afterValue, key);
      const path = buildChildPath(currentPath, key, false);
      if (!hasBefore && hasAfter) {
        entries.push({ type: "added", path, afterValue: (afterValue as Record<string, unknown>)[key] });
      } else if (hasBefore && !hasAfter) {
        entries.push({ type: "removed", path, beforeValue: (beforeValue as Record<string, unknown>)[key] });
      } else {
        walkJsonDiff((beforeValue as Record<string, unknown>)[key], (afterValue as Record<string, unknown>)[key], path, entries);
      }
    });
    return;
  }

  entries.push({
    type: "changed",
    path: currentPath,
    beforeValue,
    afterValue,
  });
}

// before/after JSON에서 path 단위 변경 목록을 계산한다.
export function buildJsonPathDiff(beforeValue: unknown, afterValue: unknown): JsonPathDiffEntry[] {
  const entries: JsonPathDiffEntry[] = [];
  walkJsonDiff(normalizeJsonValue(beforeValue), normalizeJsonValue(afterValue), "$", entries);
  return entries;
}

// 변경 목록을 added/removed/changed/하이라이트로 요약한다.
export function buildJsonDiffSummary(entries: JsonPathDiffEntry[], highlightLimit = 8): JsonDiffSummary {
  const added = entries.filter((entry) => entry.type === "added").length;
  const removed = entries.filter((entry) => entry.type === "removed").length;
  const changed = entries.filter((entry) => entry.type === "changed").length;
  const highlights = entries.slice(0, Math.max(1, highlightLimit)).map((entry) => entry.path);
  return {
    added,
    removed,
    changed,
    total: entries.length,
    highlights,
  };
}
